import { useState, useEffect, useRef, useCallback } from "react";
import LS_SERVICE from "../../../utils/localStorage";
import {
  BOOKING_ADV_PAY,
  BOOKING_ADV_PAY_VERIFY,
  BOOKING_BY_ID,
  BOOKING_CHECKIN,
  BOOKING_PAY,
  BOOKING_PAY_VERIFY,
  IMAGE_BASE_URL,
} from "../../../constants/api";
import { displayMessage, postAPI } from "../../../utils/common";
import { ERROR_MSG_TYPE, SUCCESS_MESSAGE } from "../../../constants/messages";
import { PAYMENT_METHOD, STATIC_VIDEO_LINK } from "../../../constants/constent";
import AdvancePaymentModal from "../AdvancePaymentModal";
import CustomerBookingModal from "../CustomerBookingModal";
import CheckoutModal from "../CheckoutModal";
import { Button } from "antd";

const STATUS = {
  IDLE: "idle",
  SCANNING: "scanning",
  SUCCESS: "success",
  ERROR: "error",
  LOADING: "loading",
};

const CHECKIN_STATUS = {
  CHECKIN: "checkin",
  CHECKOUT: "checkout",
};

// ── QR Detection via jsQR loaded from CDN ──────────────────────────────────
let jsQR = null;
const loadJsQR = () =>
  new Promise((res) => {
    if (jsQR) return res(jsQR);
    if (window.jsQR) {
      jsQR = window.jsQR;
      return res(jsQR);
    }
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js";
    s.onload = () => {
      jsQR = window.jsQR;
      res(jsQR);
    };
    document.head.appendChild(s);
  });

export default function QRScanner() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const overlayCanvasRef = useRef(null);

  const [status, setStatus] = useState(STATUS.IDLE);
  const [scannedData, setScannedData] = useState(null);
  const [scanMode, setScanMode] = useState(CHECKIN_STATUS.CHECKIN);
  const [torchOn, setTorchOn] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);
  const [scanCount, setScanCount] = useState(0);
  const [pulseActive, setPulseActive] = useState(false);
  const [cornerAnim, setCornerAnim] = useState(false);
  const [jsqrLoaded, setJsqrLoaded] = useState(false);

  const [isCameraOn, setIsCameraOn] = useState(false);

  const { favicon, company_name, video_link } = LS_SERVICE.get("setting") || {};

  // Load jsQR on mount
  useEffect(() => {
    loadJsQR().then(() => setJsqrLoaded(true));
  }, []);

  const stopCamera = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null; // important
      videoRef.current.src = video_link || STATIC_VIDEO_LINK; // fallback video
      videoRef.current.load(); // force reload
    }

    setIsCameraOn(false);
  }, [video_link]);

  const startCamera = useCallback(async () => {
    setStatus(STATUS.LOADING);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.src = "";
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      // Check torch
      const track = stream.getVideoTracks()[0];
      const caps = track.getCapabilities?.() ?? {};
      setHasTorch(!!caps.torch);
      setStatus(STATUS.SCANNING);
      setIsCameraOn(true);
    } catch {
      setStatus(STATUS.ERROR);
    }
  }, []);

  // Frame scanning loop
  useEffect(() => {
    if (status !== STATUS.SCANNING || !jsqrLoaded) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const video = videoRef.current;

    const tick = () => {
      if (video?.readyState === video?.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height, {
          inversionAttempts: "dontInvert",
        });
        if (code?.data) {
          handleDetected(code.data);
          return;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [status, jsqrLoaded]);

  const handleDetected = useCallback(
    (data) => {
      setStatus(STATUS.SUCCESS);
      setScannedData(data);
      setScanCount((c) => c + 1);
      setPulseActive(true);
      setCornerAnim(true);
      stopCamera();
      setTimeout(() => setPulseActive(false), 1200);
      setTimeout(() => setCornerAnim(false), 1000);
    },
    [stopCamera],
  );

  const reset = useCallback(() => {
    setStatus(STATUS.IDLE);
    setScannedData(null);
  }, []);

  // Torch toggle
  const toggleTorch = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {}
  }, [torchOn]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const isScanning = status === STATUS.SCANNING;
  const isSuccess = status === STATUS.SUCCESS;
  const isLoading = status === STATUS.LOADING;
  const isError = status === STATUS.ERROR;

  // --------------------------------------------------------------------------------
  // Handle API'S

  const [loading, setLoading] = useState(false);
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const getBookingDetail = (scanMode) => {
    setLoading(true);
    const successFn = function (data) {
      if (data.status === true) {
        const booking = data.data;
        setSelectedBooking({
          ...booking,
          page_source: "qr_scanner",
        });

        if (
          booking.booking_status === "1" ||
          booking.booking_status === "2" ||
          booking.booking_status === "3"
        ) {
          if (
            scanMode === CHECKIN_STATUS.CHECKOUT &&
            booking.booking_status === "1"
          ) {
            handleCheckoutModalOpen();
          } else {
            setBookingModalVisible(true);
          }
        } else {
          if (scanMode === CHECKIN_STATUS.CHECKIN) {
            if (
              booking.advance_amount === null ||
              parseInt(booking.advance_amount) === 0.0
            ) {
              handleAdvModalOpen(true);
            } else {
              checkin();
            }
          } else {
            // Checkout
          }
        }
        setLoading(false);
      } else {
        displayMessage(ERROR_MSG_TYPE, data.message);
        setLoading(false);
      }
    };
    const errorFn = function () {
      setLoading(false);
    };
    postAPI(BOOKING_BY_ID, { order_id: scannedData }, successFn, errorFn);
  };

  // Check-out Modal
  const [chekoutModal, setChekoutModal] = useState(false);

  // Advance Amount
  const [advModalVisible, setAdvModalVisible] = useState(false);
  const [forceCheckIn, setForceCheckIn] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const handleReset = () => {
    setBookingModalVisible(false);
    setSelectedBooking(null);
    setAdvModalVisible(false);
    setChekoutModal(false);
    setForceCheckIn(false);
    setAdvanceAmount("");
    setPaymentMethod("");
    reset();
    stopCamera();
  };

  const handleAdvModalOpen = (force_check_in) => {
    setAdvModalVisible(true);
    setForceCheckIn(force_check_in);
  };

  const handleCheckoutModalOpen = () => {
    setBookingModalVisible(false);
    setChekoutModal(true);
  };

  const checkin = () => {
    setLoading(true);
    let body = {
      booking_id: selectedBooking.id,
    };
    const successFn = function (data) {
      setLoading(false);
      displayMessage(
        data.status ? SUCCESS_MESSAGE : ERROR_MSG_TYPE,
        data.message,
      );
      handleReset();
    };
    const errorFn = function () {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to Check-In");
    };
    postAPI(BOOKING_CHECKIN, body, successFn, errorFn);
  };

  const handleAdvancePaySubmit = () => {
    if (parseFloat(advanceAmount) <= 0) {
      displayMessage(
        ERROR_MSG_TYPE,
        "Please enter a valid amount for advance payment",
      );
      return;
    }
    adv_pay_init(selectedBooking.id, advanceAmount);
  };

  const adv_pay_init = (id, advAmount) => {
    setLoading(true);
    let body = {
      id: id,
      total_amount: advAmount,
      payment_method: paymentMethod,
      force_check_in: forceCheckIn ? "yes" : "no",
    };
    const successFn = function (data) {
      if (data.status === true) {
        setLoading(false);

        if (data.payment_method === "razorpay") {
          const options = {
            key: data.options.key,
            amount: data.options.amount * 100,
            currency: data.options.currency,
            name: data.options.name,
            description: data.options.description,
            image: IMAGE_BASE_URL + data.options.image,
            order_id: data.options.order_id,
            prefill: {
              name: data.options.prefill.name,
              email: data.options.prefill.email,
              contact: data.options.prefill.contact,
            },
            handler: function (response) {
              let body = {
                ...response,
                booking_id: id,
                total_amount: advAmount,
                force_check_in: data.force_check_in,
              };
              console.log(body);
              const successFn = function (data) {
                displayMessage(SUCCESS_MESSAGE, data.message);
                checkin();
              };
              const errorFn = function () {
                setLoading(false);
                displayMessage(
                  ERROR_MSG_TYPE,
                  "Unable to do payment verification",
                );
              };
              postAPI(BOOKING_ADV_PAY_VERIFY, body, successFn, errorFn);
            },
            theme: {
              color: "#F37254",
            },
          };
          const razorpay = new window.Razorpay(options);
          razorpay.open();
        } else {
          displayMessage(SUCCESS_MESSAGE, data.message);
          handleReset();
        }
      } else {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, data.message);
      }
    };

    // Error callback
    const errorFn = function () {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to do payment");
    };

    // Make API call to your backend to initiate Razorpay
    postAPI(BOOKING_ADV_PAY, body, successFn, errorFn);
  };

  const pay_init = (id) => {
    setLoading(true);
    let body = {
      id: id,
    };
    const successFn = function (data) {
      if (data.status === true) {
        setLoading(false);
        displayMessage(SUCCESS_MESSAGE, data.message);

        const options = {
          key: data.options.key,
          amount: data.options.amount * 100,
          currency: data.options.currency,
          name: data.options.name,
          description: data.options.description,
          image: IMAGE_BASE_URL + data.options.image,
          order_id: data.options.order_id,
          prefill: {
            name: data.options.prefill.name,
            email: data.options.prefill.email,
            contact: data.options.prefill.contact,
          },
          handler: function (response) {
            let body = {
              ...response,
              booking_id: id,
            };
            console.log(body);
            const successFn = function (data) {
              displayMessage(SUCCESS_MESSAGE, data.message);
              handleReset();
            };
            const errorFn = function () {
              setLoading(false);
              displayMessage(
                ERROR_MSG_TYPE,
                "Unable to do payment verification",
              );
            };
            postAPI(BOOKING_PAY_VERIFY, body, successFn, errorFn);
          },
          theme: {
            color: "#F37254",
          },
        };
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, data.message);
      }
    };

    // Error callback
    const errorFn = function () {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to do payment");
    };

    // Make API call to your backend to initiate Razorpay
    postAPI(BOOKING_PAY, body, successFn, errorFn);
  };

  return (
    <div style={styles.root}>
      <style>{css}</style>

      {/* Background video feed */}
      <video
        ref={videoRef}
        src={!isCameraOn ? video_link || STATIC_VIDEO_LINK : undefined}
        style={{ ...styles.video, opacity: 1 }}
        playsInline
        muted
        autoPlay
        loop
      />
      <canvas ref={canvasRef} style={styles.hiddenCanvas} />

      {/* Dark overlay */}
      <div style={styles.overlay} />

      {/* ── IDLE Screen ── */}
      {status === STATUS.IDLE && (
        <div style={styles.centerContent} className="fade-in">
          <div style={styles.idleIconWrap}>
            <img style={{ height: "54px" }} src={IMAGE_BASE_URL + favicon} />
          </div>
          <h1 style={styles.idleTitle}>{company_name} Check Scanner</h1>
          <p style={styles.idleSubtitle}>
            Scan guest's QR pass for instant check-in or check-out
          </p>

          {/* Mode Toggle */}
          <div style={styles.modeToggle}>
            <button
              style={{
                ...styles.modeBtn,
                ...(scanMode === CHECKIN_STATUS.CHECKIN
                  ? styles.modeBtnActive
                  : {}),
              }}
              onClick={() => setScanMode(CHECKIN_STATUS.CHECKIN)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ marginRight: 6 }}
              >
                <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3" />
              </svg>
              Check-in
            </button>
            <button
              style={{
                ...styles.modeBtn,
                ...(scanMode === CHECKIN_STATUS.CHECKOUT
                  ? styles.modeBtnActive
                  : {}),
              }}
              onClick={() => setScanMode(CHECKIN_STATUS.CHECKOUT)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ marginRight: 6 }}
              >
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
              Check-out
            </button>
          </div>

          <button
            style={styles.startBtn}
            className="pulse-gold"
            onClick={startCamera}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ marginRight: 10 }}
            >
              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            Start Scanning
          </button>

          {scanCount > 0 && (
            <p style={styles.scanCountBadge}>
              {scanCount} scan{scanCount > 1 ? "s" : ""} today
            </p>
          )}
        </div>
      )}

      {/* ── LOADING ── */}
      {isLoading && (
        <div style={styles.centerContent}>
          <div className="spin-ring" style={styles.spinRing} />
          <p style={styles.loadingText}>Starting camera…</p>
        </div>
      )}

      {/* ── ERROR ── */}
      {isError && (
        <div style={styles.centerContent} className="fade-in">
          <div style={styles.errorIcon}>
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ef4444"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <p style={styles.errorText}>Camera access denied</p>
          <p style={styles.errorSub}>
            Please allow camera permission and try again
          </p>
          <button
            style={styles.retryBtn}
            onClick={() => {
              setStatus(STATUS.IDLE);
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* ── SCANNING UI ── */}
      {isScanning && (
        <>
          {/* Top bar */}
          <div style={styles.topBar} className="fade-in">
            <button
              style={styles.iconBtn}
              onClick={() => {
                stopCamera();
                reset();
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div style={styles.modeLabel}>
              <div
                style={{
                  ...styles.modeDot,
                  background:
                    scanMode === CHECKIN_STATUS.CHECKIN ? "#4ade80" : "#f87171",
                }}
              />
              {scanMode === CHECKIN_STATUS.CHECKIN
                ? "Check-in Mode"
                : "Check-out Mode"}
            </div>
            {hasTorch && (
              <button
                style={{
                  ...styles.iconBtn,
                  background: torchOn
                    ? "rgba(212,175,55,0.25)"
                    : "rgba(255,255,255,0.1)",
                }}
                onClick={toggleTorch}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill={torchOn ? "var(--primary-color)" : "none"}
                  stroke={torchOn ? "var(--primary-color)" : "white"}
                  strokeWidth="2"
                >
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </button>
            )}
            {!hasTorch && <div style={{ width: 40 }} />}
          </div>

          {/* Viewfinder */}
          <div style={styles.viewfinderWrap}>
            <div
              style={styles.viewfinder}
              className={cornerAnim ? "corner-flash" : ""}
            >
              <span
                style={{
                  ...styles.corner,
                  top: 0,
                  left: 0,
                  borderTop: "3px solid var(--primary-color)",
                  borderLeft: "3px solid var(--primary-color)",
                }}
              />
              <span
                style={{
                  ...styles.corner,
                  top: 0,
                  right: 0,
                  borderTop: "3px solid var(--primary-color)",
                  borderRight: "3px solid var(--primary-color)",
                }}
              />
              <span
                style={{
                  ...styles.corner,
                  bottom: 0,
                  left: 0,
                  borderBottom: "3px solid var(--primary-color)",
                  borderLeft: "3px solid var(--primary-color)",
                }}
              />
              <span
                style={{
                  ...styles.corner,
                  bottom: 0,
                  right: 0,
                  borderBottom: "3px solid var(--primary-color)",
                  borderRight: "3px solid var(--primary-color)",
                }}
              />
              <div className="scan-line" style={styles.scanLine} />
            </div>
            <p style={styles.scanHint}>Align QR code within the frame</p>
          </div>

          {/* Bottom bar */}
          <div style={styles.bottomBar} className="fade-in">
            <button
              style={styles.cancelScanBtn}
              onClick={() => {
                stopCamera();
                reset();
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {/* ── SUCCESS Result ── */}
      {isSuccess && (
        <div style={styles.resultSheet} className="slide-up">
          <div style={styles.resultHandle} />

          <div
            style={styles.successIconWrap}
            className={pulseActive ? "success-pop" : ""}
          >
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <div style={styles.resultModeTag}>
            <div
              style={{
                ...styles.modeDot,
                background:
                  scanMode === CHECKIN_STATUS.CHECKIN ? "#4ade80" : "#f87171",
                width: 8,
                height: 8,
              }}
            />
            {scanMode === CHECKIN_STATUS.CHECKIN
              ? "Check-in Scan"
              : "Check-out Scan"}
          </div>

          <h2 style={styles.resultTitle}>QR Scanned</h2>
          <p style={styles.resultSub}>Booking ID detected successfully</p>

          <div style={styles.bookingIdBox}>
            <p style={styles.bookingIdLabel}>Booking ID</p>
            <p style={styles.bookingIdVal}>{scannedData}</p>
          </div>

          <div style={styles.actionRow}>
            <Button
              style={styles.actionBtnPrimary}
              loading={loading}
              disabled={loading}
              onClick={() => {
                getBookingDetail(scanMode);
              }}
            >
              {scanMode === CHECKIN_STATUS.CHECKIN ? (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ marginRight: 8 }}
                  >
                    <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3" />
                  </svg>
                  Confirm Proceed
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ marginRight: 8 }}
                  >
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
                  </svg>
                  Confirm Proceed
                </>
              )}
            </Button>
            <Button
              loading={loading}
              disabled={loading}
              style={styles.actionBtnSecondary}
              onClick={handleReset}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ marginRight: 8 }}
              >
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
              </svg>
              Scan Again
            </Button>
          </div>

          <Button
            loading={loading}
            disabled={loading}
            style={styles.cancelResultBtn}
            onClick={reset}
          >
            Back to Home
          </Button>
        </div>
      )}

      {/* Checkout Modal */}
      {chekoutModal && selectedBooking && (
        <CheckoutModal
          visible={chekoutModal}
          onClose={handleReset}
          booking={selectedBooking} // Pass the booking data here
        />
      )}

      {bookingModalVisible && selectedBooking && (
        <CustomerBookingModal
          visible={bookingModalVisible}
          onClose={handleReset}
          booking={selectedBooking}
          handleCheckout={handleCheckoutModalOpen}
          payInit={pay_init}
          loading={loading}
        />
      )}

      {/* Advance Amount Modal */}
      {advModalVisible && (
        <AdvancePaymentModal
          visible={advModalVisible}
          onCancel={() => setAdvModalVisible(false)}
          onSubmit={handleAdvancePaySubmit}
          loading={loading}
          amount={advanceAmount}
          setAmount={setAdvanceAmount}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          PAYMENT_METHOD={PAYMENT_METHOD}
        />
      )}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = {
  root: {
    position: "relative",
    width: "100vw",
    height: "100vh",
    background: "#0a0a0f",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'DM Sans', 'Outfit', sans-serif",
  },
  video: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "opacity 0.4s",
  },
  hiddenCanvas: { display: "none" },
  overlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(5,5,15,0.55)",
    pointerEvents: "none",
  },
  centerContent: {
    position: "relative",
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: "0 32px",
    gap: 0,
  },
  idleIconWrap: {
    width: 96,
    height: 96,
    borderRadius: "50%",
    background: "#fff",
    border: "1px solid rgba(212,175,55,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 24,
  },
  idleTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 10,
    letterSpacing: "-0.5px",
  },
  idleSubtitle: {
    fontSize: 14,
    color: "rgba(255,255,255,0.45)",
    lineHeight: 1.7,
    maxWidth: 280,
    marginBottom: 32,
  },
  modeToggle: {
    display: "flex",
    gap: 8,
    background: "rgba(255,255,255,0.06)",
    borderRadius: 12,
    padding: 4,
    marginBottom: 28,
  },
  modeBtn: {
    display: "flex",
    alignItems: "center",
    background: "transparent",
    border: "none",
    color: "#fff",
    fontSize: 13,
    fontWeight: 500,
    padding: "9px 18px",
    borderRadius: 9,
    cursor: "pointer",
    transition: "all 0.2s",
    fontFamily: "inherit",
  },
  modeBtnActive: {
    background: "var(--primary-color)",
    color: "#fff",
    border: "1px solid var(--primary-color)",
  },
  startBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--primary-color)",
    color: "#fff",
    border: "none",
    borderRadius: 14,
    fontSize: 16,
    fontWeight: 700,
    padding: "16px 36px",
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "-0.2px",
    width: "100%",
    maxWidth: 280,
  },
  scanCountBadge: {
    marginTop: 16,
    fontSize: 12,
    color: "rgba(255,255,255,0.3)",
  },
  spinRing: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    border: "3px solid rgba(212,175,55,0.15)",
    borderTop: "3px solid var(--primary-color)",
    marginBottom: 16,
  },
  loadingText: { color: "rgba(255,255,255,0.5)", fontSize: 14 },
  errorIcon: { marginBottom: 16 },
  errorText: {
    color: "#ffffff",
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 8,
  },
  errorSub: { color: "rgba(255,255,255,0.4)", fontSize: 13, marginBottom: 24 },
  retryBtn: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "#ffffff",
    borderRadius: 10,
    padding: "12px 28px",
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  topBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 20,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "52px 20px 16px",
    background:
      "linear-gradient(to bottom, rgba(5,5,15,0.85) 0%, transparent 100%)",
  },
  iconBtn: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.1)",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  modeLabel: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 0.3,
  },
  modeDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
  },
  viewfinderWrap: {
    position: "relative",
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 20,
  },
  viewfinder: {
    position: "relative",
    width: 260,
    height: 260,
    borderRadius: 16,
  },
  corner: {
    position: "absolute",
    width: 28,
    height: 28,
    borderRadius: 2,
  },
  scanLine: {
    position: "absolute",
    left: 12,
    right: 12,
    height: 2,
    background: "var(--primary-color)",
    borderRadius: 2,
  },
  scanHint: {
    color: "rgba(255,255,255,0.4)",
    fontSize: 13,
    letterSpacing: 0.2,
  },
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 20,
    display: "flex",
    justifyContent: "center",
    padding: "16px 20px 48px",
    background:
      "linear-gradient(to top, rgba(5,5,15,0.85) 0%, transparent 100%)",
  },
  cancelScanBtn: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "rgba(255,255,255,0.7)",
    borderRadius: 12,
    padding: "13px 40px",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  resultSheet: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 30,
    background: "#12121c",
    borderRadius: "24px 24px 0 0",
    border: "1px solid rgba(255,255,255,0.08)",
    borderBottom: "none",
    padding: "12px 24px 48px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  resultHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    background: "rgba(255,255,255,0.12)",
    marginBottom: 28,
  },
  successIconWrap: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    background: "var(--primary-color)",
    border: "2px solid rgba(212,175,55,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
  },
  resultModeTag: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    color: "rgba(255,255,255,0.45)",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    marginBottom: 8,
  },
  resultTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 6,
    letterSpacing: "-0.4px",
  },
  resultSub: {
    fontSize: 13,
    color: "rgba(255,255,255,0.4)",
    marginBottom: 24,
  },
  bookingIdBox: {
    background: "var(--primary-color)",
    border: "1px solid rgba(212,175,55,0.18)",
    borderRadius: 12,
    padding: "14px 24px",
    textAlign: "center",
    width: "100%",
    maxWidth: 340,
    marginBottom: 24,
  },
  bookingIdLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: "#fff",
    letterSpacing: 1.2,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  bookingIdVal: {
    fontSize: 18,
    fontWeight: 700,
    color: "#fff",
    letterSpacing: 1.5,
    wordBreak: "break-all",
  },
  actionRow: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    width: "100%",
    maxWidth: 340,
    marginBottom: 12,
  },
  actionBtnPrimary: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--primary-color)",
    color: "#fff",
    border: "none",
    borderRadius: 14,
    fontSize: 15,
    fontWeight: 700,
    padding: "16px",
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "-0.2px",
  },
  actionBtnSecondary: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.06)",
    color: "#ffffff",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 14,
    fontSize: 14,
    fontWeight: 500,
    padding: "14px",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  cancelResultBtn: {
    background: "transparent",
    border: "none",
    color: "rgba(255,255,255,0.3)",
    fontSize: 13,
    cursor: "pointer",
    padding: "8px",
    fontFamily: "inherit",
  },
};

// ── Animations CSS ──────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

  @keyframes scanMove {
    0%   { top: 14px; opacity: 0; }
    5%   { opacity: 1; }
    95%  { opacity: 1; }
    100% { top: calc(100% - 16px); opacity: 0; }
  }
  .scan-line { animation: scanMove 2s ease-in-out infinite; }

  @keyframes spinAnim {
    to { transform: rotate(360deg); }
  }
  .spin-ring { animation: spinAnim 0.8s linear infinite; }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .fade-in { animation: fadeIn 0.35s ease forwards; }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
  .slide-up { animation: slideUp 0.38s cubic-bezier(0.34,1.56,0.64,1) forwards; }

  @keyframes successPop {
    0%   { transform: scale(1); }
    40%  { transform: scale(1.18); }
    70%  { transform: scale(0.93); }
    100% { transform: scale(1); }
  }
  .success-pop { animation: successPop 0.5s ease forwards; }

  @keyframes pulseGold {
    0%, 100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.4); }
    50%       { box-shadow: 0 0 0 14px rgba(212,175,55,0); }
  }
  .pulse-gold { animation: pulseGold 2s ease infinite; }

  @keyframes cornerFlash {
    0%   { filter: brightness(1); }
    50%  { filter: brightness(2.5); }
    100% { filter: brightness(1); }
  }
  .corner-flash { animation: cornerFlash 0.4s ease; }
`;
